import { useEffect, useState } from "react";
import { API, waLink, waNum, errMsg, waList, igList, addrList, hoursList } from "@/lib/api";
import WhatsAppPicker from "@/components/WhatsAppPicker";
import { usePageMeta } from "@/lib/seo";
import { toast } from "sonner";
import { Instagram, Clock, MapPin, Send } from "lucide-react";
import WaIcon from "@/components/WaIcon";

export default function Contact() {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [site, setSite] = useState(null);
  usePageMeta("Hubungi Kami", "Hubungi NAWADAH by NDH Official via WhatsApp atau Instagram — tanya harga tali PP, katrol, dan timbangan. Melayani seluruh Indonesia.");

  useEffect(() => {
    API.get("/settings").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const hours = hoursList(site);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const waWindow = window.open("", "_blank");
    try {
      await API.post("/messages", form);
      const text = `Halo NAWADAH by NDH Official, saya ${form.name} (${form.contact}).\n\n${form.message}`;
      const target = waLink(text, waNum(waList(site)[0].number));
      if (waWindow) {
        waWindow.location.href = target;
      } else {
        window.location.href = target;
      }
      toast.success("Pesan diteruskan ke WhatsApp admin kami.");
      setForm({ name: "", contact: "", message: "" });
    } catch (err) {
      if (waWindow) waWindow.close();
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-navy-dark text-white min-h-screen">
      <section className="relative py-20 bg-maritime-grid noise-overlay" data-testid="contact-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Hubungi Kami</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-14">Kontak &amp; Lokasi</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button onClick={() => setWaOpen(true)} data-testid="contact-wa" className="card-maritime-surface rounded-md p-6 hover:border-gold/50 transition-colors text-left">
                  <WaIcon className="h-7 w-7 text-[#25D366] mb-3" />
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">WhatsApp</p>
                  {waList(site).map((w, i) => (
                    <p key={i} className="font-bold text-white text-sm" data-testid={`contact-wa-number-${i}`}>{w.label || `Admin Nawadah ${i + 1}`}</p>
                  ))}
                </button>
                <a href={`https://instagram.com/${String(igList(site)[0].handle).replace(/^@/, "")}`} target="_blank" rel="noreferrer" data-testid="contact-ig" className="card-maritime-surface rounded-md p-6 hover:border-gold/50 transition-colors">
                  <Instagram className="h-6 w-6 text-gold mb-3" />
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Instagram</p>
                  {igList(site).map((g, i) => (
                    <p key={i} className="font-bold" data-testid={`contact-ig-handle-${i}`}>{g.handle}</p>
                  ))}
                </a>
                <div className="card-maritime-surface rounded-md p-6" data-testid="contact-hours">
                  <Clock className="h-6 w-6 text-gold mb-3" />
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Jam Operasional</p>
                  {hours.map((item, index) => <p key={index} className="font-bold text-sm">{item.label ? `${item.label}: ` : ""}{item.hours}</p>)}
                </div>
                <div className="card-maritime-surface rounded-md p-6" data-testid="contact-locations">
                  <MapPin className="h-6 w-6 text-gold mb-3" />
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Lokasi</p>
                  {(addrList(site).length ? addrList(site) : [{ label: "", address: "Indonesia" }]).map((a, i) => (
                    <p key={i} className="font-bold text-sm" data-testid={`contact-addr-${i}`}>{a.label ? `${a.label}: ` : ""}{a.address}</p>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {["Indonesia"].map((city) => (
                  <div key={city} className="rounded-md overflow-hidden gold-glow-border" data-testid={`map-${city.toLowerCase()}`}>
                    <iframe
                      title={`Peta ${city}`}
                      src={`https://maps.google.com/maps?q=${city}&z=4&output=embed`}
                      className="w-full h-56"
                      loading="lazy"
                    />
                    <p className="bg-navy text-center text-xs font-bold uppercase tracking-widest py-2.5 text-gold">{city}</p>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submit} className="card-maritime-surface rounded-md p-8 space-y-5" data-testid="contact-form">
              <h2 className="font-display text-2xl">Kirim Pesan Langsung</h2>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2">Nama</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  placeholder="Nama lengkap Anda"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2">No. Telepon / Email</label>
                <input
                  required
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  data-testid="contact-phone"
                  className="w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  placeholder="08xx-xxxx-xxxx"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2">Pesan</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  placeholder="Tulis pertanyaan atau kebutuhan Anda..."
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-60"
              >
                <Send className="h-4 w-4" /> {sending ? "Mengirim..." : "Kirim Pesan"}
              </button>
              <p className="text-xs text-slate-400 text-center" data-testid="contact-wa-note">
                Setelah dikirim, pesan Anda akan otomatis diteruskan ke WhatsApp admin kami dan tersimpan di sistem.
              </p>
            </form>
          </div>
        </div>
      </section>

      <WhatsAppPicker open={waOpen} onClose={() => setWaOpen(false)} contacts={waList(site)} message="Halo Admin NAWADAH, saya ingin bertanya mengenai produk dan layanan." />
    </div>
  );
}
